/**
 * Variance Analysis Dashboard  
 * Compare targets vs actuals with visualizations
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VarianceAnalysis = () => {
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [variance, setVariance] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem('token');
      const bankResponse = await axios.get(`${API_URL}/api/tenants?tenant_type=bank`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (bankResponse.data.length > 0) {
        const orgResponse = await axios.get(
          `${API_URL}/api/organizations?tenant_id=${bankResponse.data[0].id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setOrgs(orgResponse.data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const calculateVariance = async () => {
    if (!selectedOrg) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const year = new Date().getFullYear();
      const date = `${year}-01-01`;
      
      const response = await axios.get(
        `${API_URL}/api/consolidation/variance/${selectedOrg}/${year}/${date}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVariance(response.data);
    } catch (error) {
      console.error('Error:', error);
      alert('No data available for variance calculation');
    } finally {
      setLoading(false);
    }
  };

  const getVarianceColor = (pct) => {
    if (pct > 5) return 'text-green-600';
    if (pct < -5) return 'text-red-600';
    return 'text-yellow-600';
  };

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Variance Analysis</h1>
        <p className="text-gray-600 mt-1">Compare budgeted targets vs actual performance</p>
      </div>

      {/* Controls */}
      <div className="mb-6 bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-2">Organization</label>
            <select
              value={selectedOrg}
              onChange={(e) => setSelectedOrg(e.target.value)}
              className="w-full border rounded-lg px-4 py-2"
            >
              <option value="">Select organization...</option>
              {orgs.map(org => (
                <option key={org.id} value={org.id}>
                  {org.name} ({org.type})
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={calculateVariance}
              disabled={!selectedOrg || loading}
              className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-300"
            >
              {loading ? 'Calculating...' : 'Calculate Variance'}
            </button>
          </div>
        </div>
      </div>

      {/* Variance Results */}
      {variance && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Revenue Variance */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold mb-4">💰 Revenue Variance</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Target:</span>
                <span className="font-medium">ETB {(variance.revenue_target / 1000000).toFixed(2)}M</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Actual:</span>
                <span className="font-medium">ETB {(variance.revenue_actual / 1000000).toFixed(2)}M</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="font-bold">Variance:</span>
                <span className={`font-bold ${getVarianceColor(variance.revenue_variance_pct)}`}>
                  {variance.revenue_variance_pct > 0 ? '+' : ''}{variance.revenue_variance_pct.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* Expense Variance */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold mb-4">💸 Expense Variance</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Target:</span>
                <span className="font-medium">ETB {(variance.expense_target / 1000000).toFixed(2)}M</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Actual:</span>
                <span className="font-medium">ETB {(variance.expense_actual / 1000000).toFixed(2)}M</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="font-bold">Variance:</span>
                <span className="font-bold">
                  ETB {(variance.expense_variance / 1000000).toFixed(2)}M
                </span>
              </div>
            </div>
          </div>

          {/* Profit Variance */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold mb-4">📊 Profit Variance</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Target:</span>
                <span className="font-medium">ETB {(variance.profit_target / 1000000).toFixed(2)}M</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Actual:</span>
                <span className="font-medium">ETB {(variance.profit_actual / 1000000).toFixed(2)}M</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="font-bold">Variance:</span>
                <span className="font-bold">
                  ETB {(variance.profit_variance / 1000000).toFixed(2)}M
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {!variance && (
        <div className="text-center py-12 text-gray-500 bg-white rounded-lg">
          Select an organization and click "Calculate Variance" to see analysis.
        </div>
      )}

      {variance && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-bold text-blue-900 mb-2">📝 Analysis</h3>
          <p className="text-sm text-blue-800">
            {variance.revenue_variance_pct > 5 ? '✅ Revenue performing above target' :
             variance.revenue_variance_pct < -5 ? '⚠️ Revenue below target - needs attention' :
             '➡️ Revenue on track'}
          </p>
        </div>
      )}
    </div>
  );
};

export default VarianceAnalysis;